/* eslint-disable max-len */
import { Box } from '@mui/system';
import { FormikContextType, FormikErrors } from 'formik';
import React from 'react';
import { VerticalStep } from '../Stepper';
import { IStep } from '../Stepper/types';
import FormController from './highLevelComponents/FormController';
import { IFormController } from './types';
import { Error } from '@mui/icons-material';
import UserService from '../../services/UserService';
import { Permissions } from '../../lib/permissions';
import { ApplicationTitles } from '../../lib/generalTypes';
import CheckIcon from '@mui/icons-material/Check';
import VerticalEndStep, { IStepEnd } from '../Stepper/VerticalEndStep';

export interface FormInStepperBuilder {
  label: string;
  icon?: JSX.Element;
  content: IFormController[];
  index?: number;
  tooltip?: string;
}

const determineStepErrors = (step: FormInStepperBuilder, values: any, formik_errors: any) => {

  const errors = step.content.reduce((acc, curr) => {

    if (Array.isArray(curr)) {
      curr.reduce((acc, curr) => {
        if (formik_errors[curr.name] || (curr.required && !values[curr.name])) {
          acc.push(curr.name);
        }
        return acc;
      }, acc);
    }

    // if (curr.required && !values[curr.name]) {
    //   acc.push(curr.name);
    // }

    if (formik_errors[curr.name] || (curr.required && !values[curr.name])) {
      acc.push(curr.name);
    }

    return acc;
  }, [] as string[]);

  return errors;
};

const hasSpecificStepErrors = (step: FormInStepperBuilder, values: any, errors: any) => {
  const errors_found = determineStepErrors(step, values, errors);
  return errors_found.length > 0;
};

const buildForm = (bluePrint: FormInStepperBuilder, formik: FormikContextType<any>,
  isStepActive = false) => {

  const formItem = bluePrint.content.map((item) => {
    if (React.isValidElement(item)) {
      return item;
    }
    if (Array.isArray(item)) {
      return <Box display='flex'
        flexDirection={{xs: 'column', md: 'row'}}
        gap={{xs: 0, md: 10}}
        key={item?.map(x => x?.name) + 'Box'}
      >
        {item.map((subItem) => {
          return (
            <div style={subItem.hidden
              ? { display: 'none' }
              : { width: '100%' }}
            key={subItem.name}
            >
              <FormController
                {...subItem}
                disabled={subItem.disabled || !isStepActive}
                formik={formik}
              />
            </div>
          );
        })}
      </Box>;
    }

    return <FormController
      key={item.name}
      {...item}
      disabled={item.disabled || !isStepActive}
      formik={formik}
    />;
  });

  return formItem;
};

export const buildFormStep = (bluePrint: FormInStepperBuilder,
  formik: FormikContextType<any>,
  isStepActive = false) => {

  const formItem = buildForm(bluePrint, formik, isStepActive);

  const verticalStep: IStep = {
    active: isStepActive,
    label: bluePrint.label,
    icon: bluePrint.icon,
    children: formItem,
    tooltip: bluePrint.tooltip
  };

  const verticalStepComponent = <VerticalStep {...verticalStep} />;
  return verticalStepComponent;

};

export const buildFormStepEnd = (
  bluePrint: {
    icon: JSX.Element;
    index?: number;
  },
  isStepActive = false
) => {

  const verticalStepEnd: IStepEnd = {
    active: isStepActive,
    icon: bluePrint.icon,
  };

  const verticalStepEndComp = <VerticalEndStep {...verticalStepEnd} />;
  return verticalStepEndComp;
};

/** Enables next step only if previous step has no errors */
export const formInStepperBuilder = (bluePrint: FormInStepperBuilder[] | any,
  formik: FormikContextType<any>, shouldDisableSteps = true) => {

  // check if there are errors in bluePrint steps
  const hasErrors = bluePrint.reduce((acc: FormInStepperBuilder[],
    curr: FormInStepperBuilder, index: number) => {
    if (hasSpecificStepErrors(curr, formik.values, formik.errors)) {
      acc.push({
        label: curr.label,
        index,
        icon: undefined,
        content: []
      });
    }
    return acc;
  }, [] as string[]);

  // // enable next step if all fields have no errors
  // const activeStep = bluePrint.reduce((acc, curr) => {
  //   if (hasSpecificStepErrors(curr, formik.values)) {
  //     return acc;
  //   }

  //   return acc + 1;
  // }, 0);


  const formSteps = bluePrint.map((step: FormInStepperBuilder, index: number) => {
    const a = hasErrors.map((x: FormInStepperBuilder) => x.index);
    const AMin = Math.min(...a);

    const isStepActive = shouldDisableSteps
      ? index == 0
        ? true
        : index <= AMin
      : true;
    return buildFormStep(step, formik, isStepActive);
  });

  return [
    ...formSteps,
    buildFormStepEnd({
      icon: <CheckIcon/>
    }, 
    hasErrors.length === 0
    )

  ];
};

export const renderErrors = (
  errors: FormikErrors<any>,
  mergedDetails: Array<{ name: string; value: string | string[]; refValue: string }>
) => {
  return Object.keys(errors).map((error) => {
    const y = mergedDetails.find((x) => x?.refValue === error);
    return (
      <Box display='flex' gap={2}
        key={error}>
        <Error color='error' />
        <>
          <span style={{ fontWeight: 'bold' }}>{y?.name}:</span>
          {errors[error]}
        </>
      </Box>
    );
  });
};


export const mapPermissionScopeToString = (scope: string) => {
  switch (scope) {
  case 'all':
    return 'Όλοι οι οργανισμοί';
  case 'my_org':
    return 'Του οργανισμού μου';

  default:
    return '-';
  }
};


/** Find What excact application the user can see */
export function matchTitlesToPermissionsEach() {
  const returnApplicationTitlesScopes: { applicationTitle: number, applicationScope: string }[] = [];

  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationReadMy_org,
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalLocationApplication,
    applicationScope: 'my_org'
  });
  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationReadAll
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalLocationApplication,
    applicationScope: 'all'
  });

  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationModificationRequestReadMy_org,
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalLocationApplicationModificationRequest,
    applicationScope: 'my_org'
  });
  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationModificationRequestReadAll
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalLocationApplicationModificationRequest,
    applicationScope: 'all'
  });

  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationReadMy_org,
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalProfessionalsApplication,
    applicationScope: 'my_org'
  });

  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationReadAll
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalProfessionalsApplication,
    applicationScope: 'all'
  });

  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationModificationRequestReadMy_org,
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalProfessionalsApplicationModificationRequest,
    applicationScope: 'my_org'
  });

  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationModificationRequestReadAll
  ]) && returnApplicationTitlesScopes.push({
    applicationTitle: ApplicationTitles.InternalProfessionalsApplicationModificationRequest,
    applicationScope: 'all'
  });

  return returnApplicationTitlesScopes;
}

export const matchTitlesToPermissions = () => {
  const returnTitles = [];
  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationReadMy_org,
    Permissions.PermissionInternalLocationApplicationReadAll
  ]) && returnTitles.push(ApplicationTitles.InternalLocationApplication);
  UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationModificationRequestReadMy_org,
    Permissions.PermissionInternalLocationApplicationModificationRequestReadAll
  ]) && returnTitles.push(ApplicationTitles.InternalLocationApplicationModificationRequest);
  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationReadMy_org,
    Permissions.PermissionInternalProfessionalsApplicationReadAll
  ]) && returnTitles.push(ApplicationTitles.InternalProfessionalsApplication);
  UserService.hasRole([
    Permissions.PermissionInternalProfessionalsApplicationModificationRequestReadMy_org,
    Permissions.PermissionInternalProfessionalsApplicationModificationRequestReadAll
  ]) && returnTitles.push(ApplicationTitles.InternalProfessionalsApplicationModificationRequest);
  return returnTitles;
};

export const matchUnitsToPermissions = () => {
  const fetchAllUnites = UserService.hasRole([
    Permissions.PermissionInternalLocationApplicationReadAll,
    Permissions.PermissionInternalLocationApplicationModificationRequestReadAll,
    Permissions.PermissionInternalProfessionalsApplicationReadAll,
    Permissions.PermissionInternalProfessionalsApplicationModificationRequestReadAll,
  ]);
  return fetchAllUnites;
};


export function findRestApplications(titles: { value: number, key: string }[]): { applicationTitle: number, applicationScope: string | undefined }[] {

  const excludeList = Object.values(ApplicationTitles).filter((v) => !isNaN(Number(v)));


  return titles
    .filter(e => excludeList.indexOf(parseInt(String(e.value), 10)) === -1)
    .map(x => {
      return {
        applicationTitle: x.value,
        applicationScope: UserService.hasRole([Permissions.PermissionInternalRestApplicationsReadAll])
          ? 'all'
          : UserService.hasRole([Permissions.PermissionInternalRestApplicationsReadMy_org]) ?
            'my_org'
            : undefined
      };
    })
    .filter(z => z.applicationScope !== undefined);
}